import { Component } from 'react'
import * as styles from 'styles/molecules/tables/TransactionRow.css'
import { Transaction, FullMoneyflowTyped } from 'interfaces/moneyflow'
import { formatTableCurrency } from 'helpers/tableHelpers'
import { formatRelativeDate } from 'helpers/dateTimeHelpers'
import Table2, { TableContextType, TableContext } from 'components/molecules/tables/Table2'
import AbsentValue from 'components/atoms/AbsentValue'
import { TooltipIcon } from 'components/features/pfm/Tooltip/TooltipIcon'
import TextButton from 'components/atoms/buttons/TextButton'
import TransactionEditForm from 'components/molecules/forms/TransactionEditForm'
import { isTransactionEditable, isStatusCardTransaction } from 'helpers/moneyflowHelpers'
import withContext from 'hocs/withContext/withContext'
import { TransactionCategory } from 'components/features/pfm/TransactionCategory/TransactionCategory'
import { PENDING_TRANSACTIONS_HINT } from 'constants/hints'
import { TransactionName } from 'components/features/transactions/TransactionName/TransactionName'
import { TransactionDetailsPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'

type TransactionRowProps = FullMoneyflowTyped & {
  table: TableContextType
  row: Transaction
  isEditing: boolean

  onReadonlyCategoryClick: () => void
  onEditClick: () => void
  onClose: () => void
}

class TransactionRow extends Component<TransactionRowProps, {}> {
  render () {
    const { isEditing } = this.props

    return isEditing
      ? this.renderFormRow()
      : this.renderReadonlyRow()
  }

  renderDescriptionCell = () => {
    const { row } = this.props
    const { description } = row

    const simpleDescription = description ||
      (<AbsentValue>Not presented</AbsentValue>)

    return (
      <div className={ styles.descriptionCell }>
        <span>{ simpleDescription }</span>&nbsp;
        {
          row.account_source_type === 'yodlee' && row.description && row.description_verbose ? (
            <div className={ styles.tooltipIconWrapperOuter }>
              <div className={ styles.tooltipIconWrapper }>
                <TooltipIcon icon='ellipsis'>{ row.description_verbose }</TooltipIcon>
              </div>
            </div>
          ) : null
        }
      </div>
    )
  }

  renderReadonlyRow = () => {
    const { row, onReadonlyCategoryClick, onEditClick } = this.props
    const { date, amount, status } = row

    const renderActionButton = () => {
      if (isStatusCardTransaction(row)) {
        return <TransactionDetailsPaneButton text='Details' uid={ row.uid } size='micro' style='textOnly' />
      } else if (isTransactionEditable(row)) {
        return <TextButton size='regular' onClick={ onEditClick }>Edit</TextButton>
      }
      return null
    }

    const rowKey = this.props.table.getRowKey(row)

    const isPending = status === 'pending'

    return (
      <Table2.Row key={ rowKey }>
        { /* TODO a fragile place: duplicated cell names with HeadCell */ }
        <Table2.Cell rowKey={ rowKey } name='date' qaMarker='date'>
          <div className={ styles.dateWrapper }>
            { formatRelativeDate(date) }
          </div>
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='description' qaMarker='description'>
          { this.renderDescriptionCell() }
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='account__name' qaMarker='account-name'>
          <TransactionName transaction={ row } />
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='category' qaMarker='category'>
          <div className={ styles.categoryWrapper }>
            <TransactionCategory
              category={ row.category }
              onClick={ onReadonlyCategoryClick }
              dim={ isPending }
              disabled={ isPending }
              tooltipText={ isPending ? PENDING_TRANSACTIONS_HINT : undefined }
            />
          </div>
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='amount' qaMarker='amount'>
          { formatTableCurrency(amount, 'penny') }
        </Table2.Cell>
        <Table2.Cell rowKey={ rowKey } name='edit'>
          <div className={ styles.editButtonWrapper }>
            { renderActionButton() }
          </div>
        </Table2.Cell>
      </Table2.Row>
    )
  }

  renderFormRow = () => {
    const { row, type } = this.props

    return (
      <tr className={ styles.formRow }>
        <td
          className={ styles.formRowCell }
          colSpan={ 20 } // `20` is knowingly larger than any columns count
        >
          <TransactionEditForm transaction={ row } type={ type } />
        </td>
      </tr>
    )
  }
}

export default withContext(TableContext, 'table')(TransactionRow)
